import { useEffect, useState } from "react";

import { Loader2, TrendingUp, Users, Calendar, Activity } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

type BoardStats = {
  id: string;
  name: string;
  version: number;
  status: string;
  created_at: string;
  total_uses: number;
  unique_workspaces: number;
  last_used_at: string | null;
  uses_last_7_days: number;
  uses_last_30_days: number;
};

export const BoardAnalytics = () => {
  const [stats, setStats] = useState<BoardStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase
        .from("prebuilt_board_usage_stats")
        .select("*")
        .order("total_uses", { ascending: false });

      if (error) throw error;
      setStats(data || []);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const totalUses = stats.reduce((sum, s) => sum + s.total_uses, 0);
  const activeBoards = stats.filter((s) => s.total_uses > 0).length;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Uses</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUses}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Boards</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeBoards}</div>
            <p className="text-xs text-muted-foreground">With at least 1 use</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last 7 Days</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.reduce((sum, s) => sum + s.uses_last_7_days, 0)}
            </div>
            <p className="text-xs text-muted-foreground">New boards created</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last 30 Days</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.reduce((sum, s) => sum + s.uses_last_30_days, 0)}
            </div>
            <p className="text-xs text-muted-foreground">New boards created</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Board Performance</CardTitle>
          <CardDescription>Usage statistics by template board</CardDescription>
        </CardHeader>
        <CardContent>
          {stats.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No usage data available yet
            </p>
          ) : (
            <div className="space-y-4">
              {stats.map((board) => (
                <div
                  key={board.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium truncate">{board.name}</h4>
                      <Badge variant="outline" className="shrink-0">
                        v{board.version}
                      </Badge>
                      <Badge
                        variant={
                          board.status === "published"
                            ? "default"
                            : board.status === "archived"
                            ? "secondary"
                            : "outline"
                        }
                        className="shrink-0"
                      >
                        {board.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {board.total_uses} total uses
                      </span>
                      <span>{board.unique_workspaces} workspaces</span>
                      {board.last_used_at && (
                        <span>
                          Last used {new Date(board.last_used_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm shrink-0">
                    <div className="text-center">
                      <div className="font-bold">{board.uses_last_7_days}</div>
                      <div className="text-xs text-muted-foreground">7d</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold">{board.uses_last_30_days}</div>
                      <div className="text-xs text-muted-foreground">30d</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
